import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { clsx } from 'clsx';
import { FieldRenderer } from './FieldRenderer';
import type { MasterDocumentFormModalProps, MasterDocumentFormModalSection, ComputedRule, FieldError } from './MasterDocument.types';
import type { ServerRuleConfig } from './template/MasterDocumentTemplate.types';
import { evaluateCondition, extractDependencyFields } from './fieldCondition';
import type { FieldCondition } from './fieldCondition';
import { useMasterDocumentTranslation } from './i18n';
import { FieldLoadingIndicator } from './components/loading';

// ─── Icons ─────────────────────────────────────────────────

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

function ChevronDownIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ─── Computed Rules Runner ─────────────────────────────────

function ComputedRulesRunner({
  rules,
  control,
  setValue,
}: {
  rules: ComputedRule[];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
}) {
  const allWatchFields = [...new Set(rules.flatMap((r) => r.watch))];
  const watchedValues = useWatch({ control, name: allWatchFields });

  useEffect(() => {
    const snapshot: Record<string, unknown> = {};
    allWatchFields.forEach((field, idx) => { snapshot[field] = watchedValues[idx]; });
    rules.forEach((rule) => {
      const result = rule.compute(snapshot);
      setValue(rule.targetField, result, { shouldDirty: false });
    });
  }, [watchedValues, rules, setValue, allWatchFields]);

  return null;
}

// ─── Form Values Watcher ───────────────────────────────────

function FormValuesWatcher({
  control,
  onChange,
}: {
  control: ReturnType<typeof useForm>['control'];
  onChange: (values: Record<string, unknown>) => void;
}) {
  const values = useWatch({ control });
  useEffect(() => {
    const t = setTimeout(() => onChange(values as Record<string, unknown>), 100);
    return () => clearTimeout(t);
  }, [values, onChange]);
  return null;
}

// ─── Server Rules Runner ───────────────────────────────────

function ServerRulesRunner({
  rules,
  control,
  setValue,
  onEvaluate,
  setLoadingFields,
}: {
  rules: ServerRuleConfig[];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
  onEvaluate: (ruleId: string, formData: Record<string, unknown>) => Promise<Partial<Record<string, unknown>>>;
  setLoadingFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const allTriggerFields = useMemo(
    () => [...new Set(rules.flatMap((r) => r.triggerFields))],
    [rules],
  );
  const watched = useWatch({ control, name: allTriggerFields as [string, ...string[]] });
  const initialized = useRef(false);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const generations = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const isInit = !initialized.current;
    initialized.current = true;

    const snapshot: Record<string, unknown> = {};
    allTriggerFields.forEach((k, i) => { snapshot[k] = watched[i]; });

    rules.forEach((rule) => {
      if (isInit && (rule.triggerOn ?? 'change') !== 'always') return;
      if (rule.condition && !evaluateCondition(rule.condition as FieldCondition, snapshot)) return;

      const existing = timers.current.get(rule.ruleId);
      if (existing) clearTimeout(existing);

      const delay = rule.debounceMs ?? 300;
      const timer = setTimeout(async () => {
        const gen = (generations.current.get(rule.ruleId) ?? 0) + 1;
        generations.current.set(rule.ruleId, gen);

        setLoadingFields((prev) => {
          const next = new Set(prev);
          rule.targetFields.forEach((f) => next.add(f));
          return next;
        });

        try {
          const result = await onEvaluate(rule.ruleId, snapshot);
          if (generations.current.get(rule.ruleId) !== gen) return;
          Object.entries(result).forEach(([key, value]) => {
            setValue(key, value, { shouldDirty: false });
          });
        } catch {
          // Leave fields unchanged on error
        } finally {
          if (generations.current.get(rule.ruleId) === gen) {
            setLoadingFields((prev) => {
              const next = new Set(prev);
              rule.targetFields.forEach((f) => next.delete(f));
              return next;
            });
          }
        }

        timers.current.delete(rule.ruleId);
      }, delay);

      timers.current.set(rule.ruleId, timer);
    });
  }, [watched]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { timers.current.forEach((t) => clearTimeout(t)); };
  }, []);

  return null;
}

// ─── Controlled Field Wrapper ──────────────────────────────

function FieldRendererControlled({
  field,
  control,
  setValue,
  editable,
  readOnly,
  loading,
  error,
  isComputed,
}: {
  field: MasterDocumentFormModalProps['fields'][number];
  control: ReturnType<typeof useForm>['control'];
  setValue: ReturnType<typeof useForm>['setValue'];
  editable: boolean;
  readOnly: boolean;
  loading?: boolean;
  error?: FieldError;
  isComputed: boolean;
}) {
  const { t } = useMasterDocumentTranslation();
  const value = useWatch({ control, name: field.key });

  return (
    <div className="relative">
      <FieldRenderer
        fieldDef={field}
        value={value}
        onChange={(v) => setValue(field.key, v, { shouldDirty: true })}
        editable={editable}
        readOnly={readOnly}
        error={error}
      />
      {isComputed && (
        <span className="text-xs text-content-secondary dark:text-dark-content-secondary italic mt-0.5">{t('master_document.lines.calculated')}</span>
      )}
      {loading && <FieldLoadingIndicator />}
    </div>
  );
}

// ─── Accordion Section ─────────────────────────────────────

function AccordionSection({
  section,
  children,
  hasFields,
}: {
  section: MasterDocumentFormModalSection;
  children: React.ReactNode;
  hasFields: boolean;
}) {
  const [open, setOpen] = useState(section.defaultOpen !== false);

  if (!hasFields) return null;

  if (!section.collapsible) {
    return (
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border-secondary dark:border-dark-border-primary">
          <span className="text-sm font-semibold text-content-primary dark:text-dark-content-primary">
            {section.label}
          </span>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="mb-4 border border-border-secondary dark:border-dark-border-primary rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3 text-left transition-colors duration-150',
          'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
          'focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-custom-400 dark:focus:ring-dark-border-custom',
          open
            ? 'bg-background-secondary dark:bg-dark-bg-secondary'
            : 'bg-bg-primary dark:bg-dark-bg-primary',
        )}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-content-primary dark:text-dark-content-primary">
          {section.label}
        </span>
        <ChevronDownIcon
          className={clsx(
            'w-4 h-4 text-content-secondary dark:text-dark-content-secondary transition-transform duration-200',
            open ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>

      {open && (
        <div className="px-4 py-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── MasterDocumentFormModal ───────────────────────────────

/**
 * MasterDocumentFormModal — modal para crear o editar una línea del documento.
 *
 * Soporta dos layouts:
 * - Sin `sections`: grid plano de campos (comportamiento original).
 * - Con `sections`: campos agrupados en secciones verticales. Cada sección puede
 *   ser flat (collapsible: false) o tipo acordeón expandible (collapsible: true).
 *
 * @example
 * ```tsx
 * <MasterDocumentFormModal
 *   open={open}
 *   onClose={onClose}
 *   onConfirm={handleSave}
 *   fields={lineFields}
 *   title="Nueva línea"
 *   sections={[
 *     { key: "item",         label: "Datos de la línea", defaultOpen: true },
 *     { key: "segmentacion", label: "Segmentación",      defaultOpen: false, collapsible: true },
 *   ]}
 * />
 * ```
 */
export function MasterDocumentFormModal({
  open,
  onClose,
  onConfirm,
  onConfirmAndAddAnother,
  fields,
  initialData,
  computedRules,
  serverRules,
  onEvaluateServerRule,
  errors: externalErrors,
  title,
  confirmLabel,
  cancelLabel,
  docState,
  columns = 2,
  sections,
  widgetSlot,
  onFormValuesChange,
  className,
}: MasterDocumentFormModalProps) {
  const { t } = useMasterDocumentTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('master_document.chrome.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('master_document.chrome.cancel');
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);
  const modalRef = useRef<HTMLDivElement>(null);
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());

  // Collect dependency fields for condition evaluation
  const conditionDeps = useMemo(() => {
    const keys = new Set<string>();
    sortedFields.forEach((f) => {
      if (f.visibleWhen) extractDependencyFields(f.visibleWhen as FieldCondition).forEach((k) => keys.add(k));
      if (f.hiddenWhen) extractDependencyFields(f.hiddenWhen as FieldCondition).forEach((k) => keys.add(k));
    });
    return [...keys];
  }, [sortedFields]);

  const defaultValues: Record<string, unknown> = {};
  fields.forEach((f) => {
    defaultValues[f.key] = initialData?.[f.key] ?? f.defaultValue ?? '';
  });

  const { control, setValue, reset, handleSubmit } = useForm({ defaultValues });

  const watchedConditionValues = useWatch({ control, name: conditionDeps as [string, ...string[]] });

  const conditionSnapshot = useMemo(() => {
    const snap: Record<string, unknown> = {};
    conditionDeps.forEach((key, i) => { snap[key] = watchedConditionValues[i]; });
    return snap;
  }, [conditionDeps, watchedConditionValues]);

  const visibleFields = useMemo(() => {
    if (conditionDeps.length === 0) return sortedFields;
    return sortedFields.filter((f) => {
      if (f.visibleWhen && !evaluateCondition(f.visibleWhen as FieldCondition, conditionSnapshot)) return false;
      if (f.hiddenWhen && evaluateCondition(f.hiddenWhen as FieldCondition, conditionSnapshot)) return false;
      return true;
    });
  }, [sortedFields, conditionDeps.length, conditionSnapshot]);

  const configuredSectionKeys = useMemo(
    () => new Set(sections?.map((s) => s.key) ?? []),
    [sections],
  );

  // Fields without a matching section key → rendered above sections
  const commonFields = useMemo(
    () => (sections ? visibleFields.filter((f) => !configuredSectionKeys.has(f.section)) : []),
    [sections, visibleFields, configuredSectionKeys],
  );

  // Reset form when modal opens
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    const values: Record<string, unknown> = {};
    fields.forEach((f) => {
      values[f.key] = initialData?.[f.key] ?? f.defaultValue ?? '';
    });
    reset(values);
  }, [open, initialData, fields, reset]);

  // Focus first editable field on open
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const first = modalRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])',
      );
      first?.focus();
    }, 80);
    return () => clearTimeout(timer);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleConfirm = useCallback(
    (data: Record<string, unknown>) => { onConfirm(data); },
    [onConfirm],
  );

  const handleConfirmAndAddAnother = useCallback(
    (data: Record<string, unknown>) => {
      onConfirmAndAddAnother!(data);
      const emptyValues: Record<string, unknown> = {};
      fields.forEach((f) => { emptyValues[f.key] = f.defaultValue ?? ''; });
      reset(emptyValues);
    },
    [onConfirmAndAddAnother, fields, reset],
  );

  const computedTargets = new Set(computedRules?.map((r) => r.targetField) ?? []);

  const isFieldEditable = useCallback(
    (field: MasterDocumentFormModalProps['fields'][number]) => {
      if (!field.editableOn || field.editableOn.length === 0) return false;
      if (field.editableOn.includes('*')) return true;
      if (!docState) return true;
      return field.editableOn.includes(docState);
    },
    [docState],
  );

  const gridClass = columns === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2';

  function renderField(field: MasterDocumentFormModalProps['fields'][number]) {
    const isComputed = computedTargets.has(field.key);
    const isLoading = loadingFields.has(field.key);
    const editable = isFieldEditable(field) && !isComputed && !isLoading;
    return (
      <div
        key={field.key}
        className={clsx(field.span >= 2 && columns === 2 && 'sm:col-span-2')}
      >
        <FieldRendererControlled
          field={field}
          control={control}
          setValue={setValue}
          editable={editable}
          readOnly={isComputed}
          loading={isLoading}
          error={externalErrors?.[field.key]}
          isComputed={isComputed}
        />
      </div>
    );
  }

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] bg-bg-primary rounded-2xl overflow-hidden',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-h-[85vh]',
          widgetSlot ? 'max-w-[1000px]' : 'max-w-[700px]',
          'max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:translate-x-0 max-sm:translate-y-0',
          'max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:max-h-[90vh]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          className,
        )}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary dark:border-dark-border-primary">
          <h2 className="text-base font-bold text-content-primary dark:text-dark-content-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.close')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div
          className={clsx('flex', widgetSlot ? 'flex-row' : 'flex-col')}
          style={{ maxHeight: 'calc(85vh - 130px)' }}
        >
          <div className={clsx('overflow-y-auto px-6 py-5', widgetSlot ? 'flex-1 min-w-0' : 'w-full')}>
            {computedRules && computedRules.length > 0 && (
              <ComputedRulesRunner rules={computedRules} control={control} setValue={setValue} />
            )}

            {serverRules && serverRules.length > 0 && onEvaluateServerRule && (
              <ServerRulesRunner
                rules={serverRules}
                control={control}
                setValue={setValue}
                onEvaluate={onEvaluateServerRule}
                setLoadingFields={setLoadingFields}
              />
            )}

            {onFormValuesChange && (
              <FormValuesWatcher control={control} onChange={onFormValuesChange} />
            )}

            {/* ── Flat layout (no sections) ── */}
            {!sections && (
              <div className={clsx('grid gap-4', gridClass)}>
                {visibleFields.map(renderField)}
              </div>
            )}

            {/* ── Accordion layout (with sections) ── */}
            {sections && (
              <>
                {/* Common fields (no matching section key) above sections */}
                {commonFields.length > 0 && (
                  <div className={clsx('grid gap-4 mb-5', gridClass)}>
                    {commonFields.map(renderField)}
                  </div>
                )}

                {sections.map((section) => {
                  const sectionFields = visibleFields.filter((f) => f.section === section.key);
                  return (
                    <AccordionSection
                      key={section.key}
                      section={section}
                      hasFields={sectionFields.length > 0}
                    >
                      <div className={clsx('grid gap-4', gridClass)}>
                        {sectionFields.map(renderField)}
                      </div>
                    </AccordionSection>
                  );
                })}
              </>
            )}
          </div>

          {/* Widget sidebar */}
          {widgetSlot && (
            <div className="w-72 shrink-0 border-l border-border-secondary dark:border-dark-border-primary overflow-y-auto max-sm:hidden">
              {widgetSlot}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-secondary dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'text-content-tertiary hover:bg-background-secondary transition-all duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
            )}
          >
            {resolvedCancelLabel}
          </button>

          {onConfirmAndAddAnother && (
            <button
              type="button"
              onClick={handleSubmit(handleConfirmAndAddAnother)}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-semibold',
                'border border-border-primary text-content-primary hover:bg-background-secondary transition-all duration-150',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                'dark:border-dark-border-primary dark:text-dark-content-primary dark:hover:bg-dark-bg-secondary',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              )}
            >
              {t('master_document.lines.saveAndAddAnother')}
            </button>
          )}

          <button
            type="button"
            onClick={handleSubmit(handleConfirm)}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-semibold',
              'bg-primary-inverse-background text-primary-inverse-content hover:bg-primary-custom-700 transition-all duration-150',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'min-h-[36px]',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
